import * as _ from 'lodash';
import { UtilitiesService } from 'src/ng2/shared/services/utilities/utilities.service';
import { ISchool } from '../../../ng2/shared/typings/interfaces/school.interface';
import { IStudentMini } from '../../../ng2/shared/typings/interfaces/student.interface';
import { IUserMini } from '../../../ng2/shared/typings/interfaces/user.interface';
import { StudentSetService } from './../../shared/services/sorter/student-set.service';
import { TValidSdcColumns } from './../sdc-columns.type';
import {
  IColumnSort,
  IFilter,
  ISdcActivity,
  IValidJoins,
  TValidGridViews,
  TValidSdcKeys,
  TValidSdcViews,
} from './../sdc-state/sdc-state.typings';
import { sdcActionsConstant } from './sdc-actions.constant';
import { ISdcStore } from './sdc-store.interface';

interface ISetEntireFilterParams {
  newFilter: {};
  sdcKey: TValidSdcKeys;
  sdcView: TValidSdcViews;
  newLockedFilters?: {};
}

interface ISetSortParams {
  newSort: IColumnSort[];
  sdcKey: TValidSdcKeys;
  sdcView: TValidSdcViews;
}

interface ISdcInitConfig {
  activity: ISdcActivity;
  filter?: IFilter;
  sort?: IColumnSort[];
  school?: ISchool;
  gridView?: TValidGridViews;
  userAddedColumns?: TValidSdcColumns[];
}
interface INonSdcInitConfig {
  currentSchool: ISchool;
  columns: any[];
  joins: any[];
}

export class SdcActionsService {
  static $inject: string[] = [
    'flux',
    'Student',
    'StudentSet',
    'SDCStore',
    'SorterColumn',
    'backgroundJob',
    'StudentSupport',
    'UtilitiesService',
    'ErrorModal',
    'Rollbar',
  ];

  constructor(
    private flux,
    private Student,
    private studentSet: StudentSetService,
    private SDCStore: ISdcStore,
    private SorterColumn,
    private backgroundJob,
    private StudentSupport,
    private utils: UtilitiesService,
    private ErrorModal,
    private Rollbar,
  ) {}

  setFilterForColumn(columnName, newFilter): void {
    this.flux.dispatch(sdcActionsConstant.SET_FILTER_FOR_COLUMN, { columnName, newFilter });
  }

  // newLockedFilters is optional
  setEntireFilter({ newFilter, sdcKey, sdcView, newLockedFilters = null }: ISetEntireFilterParams): void {
    this.flux.dispatch(sdcActionsConstant.SET_ENTIRE_FILTER, { newFilter, newLockedFilters, sdcKey, sdcView });

    // Ensures the newFilter gets stored in the appropriate currentSdcState of the SDCStore
    this.setCurrentSdcState(sdcKey, sdcView, 'filter', newFilter);
  }

  clearFilterForColumn(columnName): void {
    this.flux.dispatch(sdcActionsConstant.CLEAR_FILTER_FOR_COLUMN, { columnName });
  }

  clearAllUnlockedFilters(): void {
    this.flux.dispatch(sdcActionsConstant.CLEAR_ALL_UNLOCKED_FILTERS);
  }

  addFilters(newFilters): void {
    this.flux.dispatch(sdcActionsConstant.ADD_FILTERS, { newFilters });
  }

  addLockedFilters(newLocks): void {
    this.flux.dispatch(sdcActionsConstant.ADD_LOCKED_FILTERS, { newLocks });
  }

  setLockedFilters(colNames: TValidSdcColumns[]): void {
    this.flux.dispatch(sdcActionsConstant.SET_LOCKED_FILTERS, { lockedFilters: colNames });
  }

  unlockAllFilters(): void {
    this.flux.dispatch(sdcActionsConstant.UNLOCK_ALL_FILTERS);
  }

  setSelectedStudentIds(ids): void {
    this.flux.dispatch(sdcActionsConstant.SET_SELECTED_STUDENT_IDS, { ids });
  }

  selectAllStudents(): void {
    this.flux.dispatch(sdcActionsConstant.SELECT_ALL_STUDENTS);
  }

  selectFilteredStudents(): void {
    this.flux.dispatch(sdcActionsConstant.SELECT_FILTERED_STUDENTS);
  }

  unselectAllStudents(): void {
    this.flux.dispatch(sdcActionsConstant.UNSELECT_ALL_STUDENTS);
  }

  setRecentEditedStudentIds(_ids): void {
    this.flux.dispatch(sdcActionsConstant.SET_RECENT_EDITED_STUDENT_IDS, { _ids });
  }

  setCurrentSdcState(sdcKey, sdcView, property, value) {
    this.flux.dispatch(sdcActionsConstant.SET_CURRENT_SDC_STATE, { sdcKey, sdcView, property, value });
  }

  resetFlattenedStudents(): void {
    this.flux.dispatch(sdcActionsConstant.RESET_FLATTENED_STUDENTS);
  }

  resetCurrentSdcState(): void {
    this.flux.dispatch(sdcActionsConstant.RESET_CURRENT_SDC_STATE);
  }

  /**
   * Fetch students, then load them into the SDCStore with an optional filter and sort
   *
   * @param school {School} school to load students from
   * @param columnKeys {Array|String} list of columns to load
   * @param filter {Object} optional filter to load after students are fetched
   * @param sort {Array|Object} optional sort to load after students are fetched
   * @param hiddenColumnKeys {Array|String} optional columnKeys to hide from the grid
   */
  fetchStudentsAsync(
    school: ISchool,
    columnKeys: TValidSdcColumns[],
    joins?: IValidJoins[],
    filter?: any,
    sort?: IColumnSort[],
    hiddenColumnKeys?: TValidSdcColumns[],
    bundleName?: string,
  ) {
    this.flux.dispatch(sdcActionsConstant.UPDATE_UI_STATE, { isLoading: true });
    const loadedProjection = this.SDCStore.getLoadedProjection();
    const loadedJoins = this.SDCStore.getLoadedJoins();
    const schoolId = school._id;
    return this.studentSet
      .fetchStudents({
        schoolId,
        columnKeys,
        joins,
        loadedProjection,
        loadedJoins,
        bundleName,
      })
      .then(ret => {
        // If no reload is needed, ret.noNewData will be true, return with
        // already loaded students
        let students;
        if (ret.noNewData) {
          students = this.SDCStore.getAllStudents();
        } else {
          students = ret.students;
        }

        this.flux.dispatch(sdcActionsConstant.SET_STUDENTS_COLUMN_KEYS_SCHOOL_SORT_AND_FILTER, {
          students,
          columnKeys,
          joins,
          filter,
          school,
          sort,
          hiddenColumnKeys,
          studentsProjection: ret.projection,
          studentsJoins: ret.joins,
        });
      })
      .finally(() => {
        this.flux.dispatch(sdcActionsConstant.UPDATE_UI_STATE, { isLoading: false });
      });
  }

  /**
   * Initializes the SDCStore with just a school and clears everything else out
   *
   * @param school
   */
  initializeWithSchool(school: ISchool) {
    this.flux.dispatch(sdcActionsConstant.SET_STUDENTS_COLUMN_KEYS_SCHOOL_SORT_AND_FILTER, {
      students: [],
      columnKeys: [],
      projection: [],
      joins: [],
      filter: {},
      school,
      sort: [],
      hiddenColumnKeys: [],
      studentsProjection: {},
      studentsJoins: [],
    });
    this.flux.dispatch(sdcActionsConstant.UPDATE_UI_STATE, { currentActivity: { state: null } });
  }

  /**
   * Initializes the SDCStore for a particular sdcActivityConstant
   *
   * Sets ui.currentActivity.state, filter, columnKeys, and sort based on the activity. It also fetches
   * student data, if needed.
   *
   * @param activity the activity from the sdcActivityConstant constant (eg, sdcActivityConstant.GRAD_PLANNING)
   * @param filter optional filter override
   * @param sort optional sort override
   * @param school optional School (by default uses the existing school in the SDCStore)
   * @param {Array} of additional columns to hide for an activity
   */
  initializeSdcActivityAsync({ activity, filter, sort, school, gridView, userAddedColumns = [] }: ISdcInitConfig) {
    school = school || this.SDCStore.getSchool();
    const gridViews = activity.gridViews && activity.gridViews[gridView];

    let columns;
    let hiddenColumns;

    if (gridViews) {
      columns = gridViews;
      hiddenColumns = _.difference(activity.columns, gridViews);
    } else {
      columns = activity.columns;
      hiddenColumns = activity.hiddenColumns;
    }

    // Add any user added columns
    columns = columns.concat(userAddedColumns);

    let joins = this.utils.copyPOJO(activity.joins || []);

    this.flux.dispatch(sdcActionsConstant.SET_SDC_ACTIVITY, { state: activity.state });

    return this.fetchStudentsAsync(school, columns, joins, filter, sort, hiddenColumns, activity.bundleName);
  }

  /**
   * Initializes the SDCStore for a non-SDC related content
   */
  initializeNonSdcActivityAsync(params: INonSdcInitConfig) {
    const { currentSchool, columns, joins } = params;
    const school = currentSchool || this.SDCStore.getSchool();
    const filter = null;
    const sort = null;
    const hiddenColumns = null;
    const bundleName = null;

    return this.fetchStudentsAsync(school, columns, joins, filter, sort, hiddenColumns, bundleName);
  }

  // fetches additional columns, appending them to the end of the current columns
  fetchAdditionalColumnsAsync(additionalColumnKeys) {
    this.flux.dispatch(sdcActionsConstant.UPDATE_UI_STATE, { isLoading: true });

    const schoolId = this.SDCStore.getSchool()._id;
    const columnKeys = additionalColumnKeys;
    const projection = [];
    const joins = [];
    const forceDataRefetch = true;

    return this.studentSet
      .fetchStudents({
        schoolId,
        columnKeys,
        projection,
        joins,
        forceDataRefetch,
      })
      .then(ret => {
        // TODO: bug: sometimes ret.students is undefined?
        // I think this is related to scenario when ret === { noNewData: true }
        // Need to figure out how to load new columns when no new students are fetched
        // Possibly new method needed
        this.flux.dispatch(sdcActionsConstant.APPEND_COLUMN_DATA, {
          students: ret.students,
          columnKeys: additionalColumnKeys,
          projection: ret.projection,
        });
      })
      .finally(() => {
        this.flux.dispatch(sdcActionsConstant.UPDATE_UI_STATE, { isLoading: false });
      });
  }

  appendUserAddedColumns(sdcKey: TValidSdcKeys, sdcView: TValidSdcViews, columnsToAdd: TValidSdcColumns[]) {
    this.flux.dispatch(sdcActionsConstant.MERGE_USER_ADDED_COLUMNS, {
      sdcKey,
      sdcView,
      columnsToAdd,
    });
  }

  // TODO: DELETE??? Doesn't seem to be used
  refetchColumnsThatDependOnAsync(studentIds, paths) {
    const columnKeys = this.SDCStore.getColumnKeys();
    const columnsToRefresh = _.filter(columnKeys, (col: string) => this.SorterColumn.getByColumnKey(col).joins);

    if (studentIds) {
      throw new Error('refetching individual students is not supported');
    }

    if (paths && paths.length > 0) {
      throw new Error('paths not implemented');
    }

    return this.fetchAdditionalColumnsAsync(columnsToRefresh);
  }

  /**
   * Updates part of one or more students based on a path
   *
   * The difference with this and updateStudentFieldByPathAsync is that this does *not*
   * make any request to the server. This should be used only to record updates where other pieces of code
   * are making the server request.
   *
   * @param _ids
   * @param path
   * @param newValue
   */
  recordStudentFieldUpdate(_ids, path, newValue, bulk) {
    this.flux.dispatch(sdcActionsConstant.UPDATE_STUDENT_FIELD_BY_PATH, { _ids, path, newValue, bulk });
  }

  /**
   * Updates part of one or more students based on a path
   *
   * The difference with this and updateStudentFieldByPathAsync is that this does *not*
   * make any request to the server. This should be used only to record updates where other pieces of code
   * are making the server request.
   *
   * @param patches
   * @param origin - if 'sdc' (default), updates ngrx store as well; if 'ngrx', won't update ngrx store
   */
  recordMultiStudentUpdate(patches, origin = 'sdc') {
    this.flux.dispatch(sdcActionsConstant.MULTI_STUDENT_UPDATE, { patches, origin });
  }

  /**
   * Updates part of one or more students based on a path
   *
   * @param _ids {Array|String} one or more student _ids
   * @param path {String} dot-notation path to update
   * @param newValue {*} the new value
   * @param options optional options
   *    immediate - if set to true, causes the SDCStore student(s) to be updated before the request even resolves
   * @return {Promise} resolves/rejects with the HTTP response from the server
   */
  updateStudentFieldByPathAsync(_ids, path, newValue, options) {
    if (!_.isArray(_ids)) {
      _ids = [_ids];
    }

    options = options || {};
    const bulk = options.bulk;

    if (options.immediate) {
      this.recordStudentFieldUpdate(_ids, path, newValue, bulk);
    }

    const saveId = this.utils.createV4Uuid();
    const save = { _ids, path };

    this.flux.dispatch(sdcActionsConstant.ADD_PENDING_SAVE, { _id: saveId, pendingSave: save });

    // Updates the students if they were not immediately updated, and removes the pending save
    const updateStudentsAndRemoveSave = () => {
      if (!options.immediate) {
        this.recordStudentFieldUpdate(_ids, path, newValue, bulk);
      }
      this.flux.dispatch(sdcActionsConstant.REMOVE_PENDING_SAVE, { _id: saveId });
    };

    if (_ids.length === 1) {
      return this.Student.singleUpdate(_ids, path, newValue)
        .then(response => {
          updateStudentsAndRemoveSave();
          return response;
        })
        .catch(error => {
          // PATHS_TO_NOT_LOG_TO_ROLLBAR are paths that, if an error occurs while patching,
          // we do not want to log to Rollbar. This might be the case because we are preventing
          // certain key-value combinations on the backend from being valid patches but want to
          // message to the user what the issue is so that they can take an appropriate action
          // without the need to log to Rollbar.
          const PATHS_NOT_TO_LOG_TO_ROLLBAR = ['gradPlanningDetails.plannedDiplomaType'];
          const _error = this.utils.copyPOJO(error);
          _error.status = `Error on _id of ${_ids[0]}. ` + _error.status;

          this.flux.dispatch(sdcActionsConstant.REMOVE_PENDING_SAVE, { _id: saveId });

          if (_.includes(PATHS_NOT_TO_LOG_TO_ROLLBAR, path)) {
            this.ErrorModal.showAndRequireRefreshNoRollbar('Error updating student', _error);
          } else {
            this.ErrorModal.showAndRequireRefresh('Error updating student', _error);
          }
        });
    } else {
      return this.Student.bulkUpdate(_ids, path, newValue)
        .then(response => {
          if (response.headers('nv-background-jobs')) {
            const backgroundJobPromise = this.backgroundJob(response.headers('nv-background-jobs')).promise();
            return backgroundJobPromise.then(() => {
              updateStudentsAndRemoveSave();
              return backgroundJobPromise;
            });
          } else {
            updateStudentsAndRemoveSave();
            return response;
          }
        })
        .catch(() => {
          this.flux.dispatch(sdcActionsConstant.REMOVE_PENDING_SAVE, { _id: saveId });
        });
    }
  }

  /**
   *
   * @param patches {Array} an array of patches that look like this:
   *    @example Patches
   *       [
   *          {
   *             _ids: ['800009', '74638284'],
   *             path: 'nextScheduledRegents.ccEla.adminDate',
   *             newValue: '2017-06-22'
   *          }
   *       ]
   * @param options
   */
  multiUpdateStudentAsync(patches, options?) {
    options = options || {};

    if (options.immediate) {
      this.recordMultiStudentUpdate(patches);
    }

    const saveId = this.utils.createV4Uuid();
    const save = {
      patches,
    };

    this.flux.dispatch(sdcActionsConstant.ADD_PENDING_SAVE, { _id: saveId, pendingSave: save });

    const updateStudentsAndRemoveSave = () => {
      if (!options.immediate) {
        this.recordMultiStudentUpdate(patches);
      }
      this.flux.dispatch(sdcActionsConstant.REMOVE_PENDING_SAVE, { _id: saveId });
    };

    return this.Student.multiBulkUpdate(patches)
      .then(response => {
        if (response.headers('nv-background-jobs')) {
          const backgroundJobPromise = this.backgroundJob(response.headers('nv-background-jobs')).promise();
          return backgroundJobPromise.then(() => {
            updateStudentsAndRemoveSave();
            return backgroundJobPromise;
          });
        } else {
          updateStudentsAndRemoveSave();
          return response;
        }
      })
      .catch(() => {
        this.flux.dispatch(sdcActionsConstant.REMOVE_PENDING_SAVE, { _id: saveId });
      });
  }

  /**
   * This method will assign a support to multiple students, creating a studentSupport   * for each student.
   *
   * If a student already has a studentSupport for the support, it will not be added to
   * avoid duplicates.
   *
   * TODO: any components/services that currently call this method should be refactored to dispatch
   * new BulkCreateStudentSupports() action. See https://www.pivotaltracker.com/story/show/162624741
   *
   * @param {*} _ids - array of studentIds that will be assigned the support
   * @param {*} support - the support being assigned to each student in studentIds
   */

  createStudentSupportsAsync(_ids, support) {
    const studentIdsToStudentSupportIds = this.StudentSupport.createStudentSupportIdsFromStudentIds(_ids);
    const studentSupport = this.StudentSupport.createTemplateFromSupport(support);

    return this.StudentSupport.bulkCreate(studentIdsToStudentSupportIds, studentSupport).then(response => {
      if (!response.config.data.studentSupportIds) {
        const msg = `The student(s) you are trying to add to the ${support.name}
          have already been assigned to this support. No duplicates are allowed`;
        this.Rollbar.debug(msg);
        console.warn(msg);
      }

      if (response.headers('nv-background-jobs')) {
        const backgroundJobPromise = this.backgroundJob(response.headers('nv-background-jobs')).promise();

        return backgroundJobPromise.then(() => {
          const studentKey = 'student';
          const studentMini: IStudentMini = {
            studentId: null,
            lastFirst: null,
          };
          studentSupport[studentKey] = studentMini;
          this.flux.dispatch(sdcActionsConstant.ADD_STUDENT_SUPPORTS, {
            studentIdsToStudentSupportIds,
            studentSupport,
          });
          return backgroundJobPromise;
        });
      } else {
        return response;
      }
    });
  }

  /**
   * This method will update school in SDCStore & refetch any columns dependent on the school data
   * @param patch {Object}
   * @param path {string}
   */
  updateSchoolByPatch(patch) {
    this.flux.dispatch(sdcActionsConstant.UPDATE_SCHOOL_BY_PATCH, { patch });
    const columnKeys = this.SDCStore.getColumnKeys();
    const columnsToRefresh = _.filter(columnKeys, (col: string) => {
      const dependencies = this.SorterColumn.getByColumnKey(col).dependencies;
      return _.includes(dependencies, 'school');
    });

    // Need to refetch data for any column on the student that could have been affected by the
    // school update & related background job
    return this.fetchAdditionalColumnsAsync(columnsToRefresh);
  }

  addCourseDiff(courseDiff) {
    const { studentId, _id: courseDiffId } = courseDiff;
    const sdcPayload = {
      studentIdsToCourseDiffIds: { [studentId]: courseDiffId },
      courseDiff,
    };
    this.flux.dispatch(sdcActionsConstant.ADD_COURSE_DIFFS, sdcPayload);
  }

  addGapPlan(gapPlan) {
    const { studentId, _id: gapPlanId } = gapPlan;
    const sdcPayload = {
      studentIdsToGapPlanIds: { [studentId]: gapPlanId },
      gapPlan,
    };
    this.flux.dispatch(sdcActionsConstant.ADD_GAP_PLANS, sdcPayload);
  }

  addStudentSupport(studentSupport) {
    this.flux.dispatch(sdcActionsConstant.ADD_STUDENT_SUPPORTS, studentSupport);
  }

  addSchoolSupport(data) {
    this.flux.dispatch(sdcActionsConstant.ADD_SCHOOL_SUPPORT, data);
  }

  updateGapPlan(gapPlan) {
    this.flux.dispatch(sdcActionsConstant.UPDATE_GAP_PLAN, gapPlan);
  }

  updateCourseDiff(courseDiff) {
    this.flux.dispatch(sdcActionsConstant.UPDATE_COURSE_DIFF, courseDiff);
  }

  updateSchoolSupport(schoolSupport) {
    this.flux.dispatch(sdcActionsConstant.UPDATE_SCHOOL_SUPPORT, schoolSupport);
  }

  updateStudentSupports(data: { studentSupportIds: string[]; patch: { status: string } }) {
    const { studentSupportIds, patch } = data;
    this.flux.dispatch(sdcActionsConstant.UPDATE_STUDENT_SUPPORTS, {
      studentSupportIds,
      patch,
    });
  }

  updateStudentSupportsAsync(_ids, field, studentSupports) {
    return this.StudentSupport.bulkUpdate(_ids, field).then(response => {
      if (response.headers('nv-background-jobs')) {
        const backgroundJobPromise = this.backgroundJob(response.headers('nv-background-jobs')).promise();
        return backgroundJobPromise.then(() => {
          const patch = { status: field };
          _.each(studentSupports, (studentSupport: any) => {
            const studentSupportIds = [];
            studentSupportIds.push(studentSupport._id);
            this.flux.dispatch(sdcActionsConstant.UPDATE_STUDENT_SUPPORTS, {
              studentSupportIds,
              patch,
            });
          });
          return backgroundJobPromise;
        });
      } else {
        return response;
      }
    });
  }

  /**
   * Sets the sort order
   *
   * @param sort {Array|Object} A sort order like `[ { colId: 'studentId', sort: 'asc' } ]`
   */
  setSort({ newSort, sdcView, sdcKey }: ISetSortParams) {
    this.flux.dispatch(sdcActionsConstant.SET_SORT, { sort: newSort });

    // Ensures the newFilter gets stored in the appropriate currentSdcState of the SDCStore
    this.setCurrentSdcState(sdcKey, sdcView, 'sort', newSort);
  }

  /**********************************************************************************************
   ****** USER METHODS  *************************************************************************
   **********************************************************************************************/

  setAllSchoolUsers(allSchoolUsers: IUserMini[]): void {
    this.flux.dispatch(sdcActionsConstant.SET_ALL_SCHOOL_USERS, { allSchoolUsers });
  }

  setAllSchoolSupports(allSchoolSupports: any): void {
    this.flux.dispatch(sdcActionsConstant.SET_ALL_SCHOOL_SUPPORTS, allSchoolSupports);
  }

  setAllStudentSupports(allStudentSupports: any): void {
    this.flux.dispatch(sdcActionsConstant.SET_ALL_STUDENT_SUPPORTS, allStudentSupports);
  }

  setAllStudents(allStudents: any): void {
    this.flux.dispatch(sdcActionsConstant.SET_ALL_STUDENTS, allStudents);
  }
}
